import React, { useState, useEffect } from "react";
import { Container } from "components";
import api from "services/api";
import { useParams, useNavigate } from "react-router-dom";
import { useToaster } from "hooks/useToaster";

const AddPlan = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { setSuccessToaster, setErrorToaster } = useToaster();
  
  const [formData, setFormData] = useState({
    membership_id: "",
    user_id: userId,
    promocode: "",
    billing_name: "",
    billing_email: "",
    billing_phone: "",
    billing_address: "",
    billing_state_name: "",
    billing_country_id: "95",
    billing_gstin: "",
    billing_pan: "",
    first_name: [""],
    middle_name: [""],
    last_name: [""],
    phone_no: [""],
    email: [""],
    dob: [""],
    sex: [""],
    relation: [""],
    nationality: ["95"]
  });

  const [membershipPlans, setMembershipPlans] = useState([]);
  const [relations, setRelations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    fetchMembershipPlans();
    fetchRelations();
    fetchCountries();
    
    // Handle browser autofill
    const handleAutofill = () => {
      // Get all form inputs and check for autofilled values
      const inputs = document.querySelectorAll('input, select');
      inputs.forEach((input) => {
        if (input.value && input.value !== formData[input.name]) {
          // Trigger change event to update form state
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        }
      });
    };
    
    // Check for autofill after a short delay
    const autofillTimer = setTimeout(handleAutofill, 100);
    
    // Listen for animation events that indicate autofill
    const handleAnimationStart = (e) => {
      if (e.animationName === 'onAutoFillStart') {
        handleAutofill();
      }
    };
    
    document.addEventListener('animationstart', handleAnimationStart);
    
    return () => {
      clearTimeout(autofillTimer);
      document.removeEventListener('animationstart', handleAnimationStart);
    };
  }, []);

  const fetchMembershipPlans = async () => {
    try {
      const response = await api.get("/membership-plans");
      setMembershipPlans(response?.data?.data?.membershipPlans || []);
    } catch (error) {
      console.log("Error fetching membership plans:", error);
    }
  };

  const fetchRelations = async () => {
    try {
      const response = await api.get("/relations");
      setRelations(response?.data?.data?.relations || []);
    } catch (error) {
      console.log("Error fetching relations:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get("/countries");
      setCountries(response?.data?.data?.countries || []);
    } catch (error) {
      console.log("Error fetching countries:", error);
    }
  };

  const handleInputChange = (field, value, index = null) => {
    if (index !== null) {
      const newArray = [...formData[field]];
      newArray[index] = value;
      setFormData({ ...formData, [field]: newArray });
    } else {
      setFormData({ ...formData, [field]: value });
      
      // If membership_id is changed, only remove excess members if current count exceeds plan limit
      if (field === "membership_id") {
        const selectedPlan = membershipPlans.find(plan => plan.id.toString() === value);
        if (selectedPlan) {
          const currentMemberCount = formData.first_name.length;
          const maxMembers = selectedPlan.no_of_users;
          
          if (currentMemberCount > maxMembers) {
            // Remove excess members only if current count exceeds plan limit
            setFormData(prev => ({
              ...prev,
              first_name: prev.first_name.slice(0, maxMembers),
              middle_name: prev.middle_name.slice(0, maxMembers),
              last_name: prev.last_name.slice(0, maxMembers),
              phone_no: prev.phone_no.slice(0, maxMembers),
              email: prev.email.slice(0, maxMembers),
              dob: prev.dob.slice(0, maxMembers),
              sex: prev.sex.slice(0, maxMembers),
              relation: prev.relation.slice(0, maxMembers),
              nationality: prev.nationality.slice(0, maxMembers)
            }));
          }
        }
      }
    }
  };

  const addMember = () => {
    const selectedPlan = membershipPlans.find(plan => plan.id.toString() === formData.membership_id);
    const maxMembers = selectedPlan ? selectedPlan.no_of_users : 1;
    
    if (formData.first_name.length < maxMembers) {
      setFormData({
        ...formData,
        first_name: [...formData.first_name, ""],
        middle_name: [...formData.middle_name, ""],
        last_name: [...formData.last_name, ""],
        phone_no: [...formData.phone_no, ""],
        email: [...formData.email, ""],
        dob: [...formData.dob, ""],
        sex: [...formData.sex, ""],
        relation: [...formData.relation, ""],
        nationality: [...formData.nationality, "95"]
      });
    }
  };

  const removeMember = (index) => {
    if (formData.first_name.length > 1) {
      setFormData({
        ...formData,
        first_name: formData.first_name.filter((_, i) => i !== index),
        middle_name: formData.middle_name.filter((_, i) => i !== index),
        last_name: formData.last_name.filter((_, i) => i !== index),
        phone_no: formData.phone_no.filter((_, i) => i !== index),
        email: formData.email.filter((_, i) => i !== index),
        dob: formData.dob.filter((_, i) => i !== index),
        sex: formData.sex.filter((_, i) => i !== index),
        relation: formData.relation.filter((_, i) => i !== index),
        nationality: formData.nationality.filter((_, i) => i !== index)
      });
    }
  };

  const validateForm = () => {
    const errors = [];
    
    // Debug: Log current form data
    console.log("Form data during validation:", formData);

    // Plan selection validation
    if (!formData.membership_id) {
      errors.push("Please select a membership plan");
    }

    // Billing details validation
    if (!formData.billing_name.trim()) {
      errors.push("Billing name is required");
    }
    if (!formData.billing_email.trim()) {
      errors.push("Billing email is required");
    } else if (!/\S+@\S+\.\S+/.test(formData.billing_email)) {
      errors.push("Please enter a valid billing email");
    }
    if (!formData.billing_phone.trim()) {
      errors.push("Billing phone is required");
    }
    if (!formData.billing_address.trim()) {
      errors.push("Billing address is required");
    }
    if (!formData.billing_state_name.trim()) {
      errors.push("Billing state is required");
    }
    if (!formData.billing_country_id) {
      errors.push("Billing country ID is required");
    }

    // Member details validation
    formData.first_name.forEach((firstName, index) => {
      if (!firstName.trim()) {
        errors.push(`Member ${index + 1}: First name is required`);
      }
    });

    formData.phone_no.forEach((phone, index) => {
      if (!phone || phone.toString().trim() === '') {
        errors.push(`Member ${index + 1}: Phone number is required`);
      }
    });

    formData.email.forEach((email, index) => {
      if (!email.trim()) {
        errors.push(`Member ${index + 1}: Email is required`);
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        errors.push(`Member ${index + 1}: Please enter a valid email`);
      }
    });

    formData.dob.forEach((dob, index) => {
      if (!dob) {
        errors.push(`Member ${index + 1}: Date of birth is required`);
      }
    });

    formData.sex.forEach((sex, index) => {
      if (!sex) {
        errors.push(`Member ${index + 1}: Sex is required`);
      }
    });

    formData.relation.forEach((relation, index) => {
      if (!relation) {
        errors.push(`Member ${index + 1}: Relation is required`);
      }
    });

    formData.nationality.forEach((nationality, index) => {
      if (!nationality || nationality.toString().trim() === '') {
        errors.push(`Member ${index + 1}: Nationality is required`);
      }
    });

    return errors;
  };

  const showNextError = () => {
    if (validationErrors.length > 0 && currentErrorIndex < validationErrors.length) {
      setErrorToaster(validationErrors[currentErrorIndex]);
      setCurrentErrorIndex(prev => prev + 1);
    }
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    // Convert from YYYY-MM-DD to DD-MM-YYYY format with zero padding
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If we have validation errors and haven't shown all yet, show next error
    if (validationErrors.length > 0 && currentErrorIndex < validationErrors.length) {
      showNextError();
      return;
    }
    
    // Validate form
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setCurrentErrorIndex(0);
      setErrorToaster(errors[0]);
      setCurrentErrorIndex(1);
      return;
    }

    setLoading(true);

    try {
      // Format the data for API submission
      const apiData = {
        ...formData,
        dob: formData.dob.map(date => formatDateForAPI(date))
      };
      
      await api.post("/membership-application", apiData);
      setSuccessToaster("Plan added successfully!");
      navigate(`/membershipDetails/${userId}`);
    } catch (error) {
      console.log("Error submitting form:", error);
      
      // Handle API validation errors
      if (error.response?.data?.errors) {
        const apiErrors = Object.values(error.response.data.errors).flat();
        setValidationErrors(apiErrors);
        setCurrentErrorIndex(0);
        setErrorToaster(apiErrors[0]);
        setCurrentErrorIndex(1);
      } else if (error.response?.data?.message) {
        setErrorToaster(error.response.data.message);
      } else {
        setErrorToaster("Error adding plan. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <style>
        {`
          @keyframes onAutoFillStart {
            from { /**/ }
            to { /**/ }
          }
          input:-webkit-autofill {
            animation-name: onAutoFillStart;
            animation-duration: 0.001s;
          }
        `}
      </style>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <h4 className="card-header">Add Plan</h4>
          
          <form onSubmit={handleSubmit}>
            {/* Membership Plan Selection */}
            <div className="card mb-4">
              <div className="card-header">
                <h5>Plan Selection</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Membership Plan <span style={{color: 'red'}}>*</span></label>
                    <select
                      className="form-select"
                      value={formData.membership_id}
                      onChange={(e) => handleInputChange("membership_id", e.target.value)}
                    >
                      <option value="">Select Plan</option>
                      {membershipPlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Promocode (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.promocode}
                      onChange={(e) => handleInputChange("promocode", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Details */}
            <div className="card mb-4">
              <div className="card-header">
                <h5>Billing Details</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing Name <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_name}
                      onChange={(e) => handleInputChange("billing_name", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing Email <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.billing_email}
                      onChange={(e) => handleInputChange("billing_email", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing Phone <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_phone}
                      onChange={(e) => handleInputChange("billing_phone", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing Address <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_address}
                      onChange={(e) => handleInputChange("billing_address", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing State <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_state_name}
                      onChange={(e) => handleInputChange("billing_state_name", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing Country <span style={{color: 'red'}}>*</span></label>
                    <select
                      className="form-select"
                      value={formData.billing_country_id}
                      onChange={(e) => handleInputChange("billing_country_id", e.target.value)}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing GSTIN (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_gstin}
                      onChange={(e) => handleInputChange("billing_gstin", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Billing PAN (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.billing_pan}
                      onChange={(e) => handleInputChange("billing_pan", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Member Details */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5>Member Details</h5>
                  {formData.membership_id && (
                    <small className="text-muted">
                      {(() => {
                        const selectedPlan = membershipPlans.find(plan => plan.id.toString() === formData.membership_id);
                        return selectedPlan ? `Plan allows ${selectedPlan.no_of_users} member(s)` : '';
                      })()}
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={addMember}
                  disabled={(() => {
                    const selectedPlan = membershipPlans.find(plan => plan.id.toString() === formData.membership_id);
                    const maxMembers = selectedPlan ? selectedPlan.no_of_users : 1;
                    return formData.first_name.length >= maxMembers;
                  })()}
                >
                  Add Member
                </button>
              </div>
              <div className="card-body">
                {formData.first_name.map((_, index) => (
                  <div key={index} className="border p-3 mb-3 rounded">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Member {index + 1}</h6>
                      {formData.first_name.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeMember(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">First Name <span style={{color: 'red'}}>*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.first_name[index]}
                          onChange={(e) => handleInputChange("first_name", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Middle Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.middle_name[index]}
                          onChange={(e) => handleInputChange("middle_name", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.last_name[index]}
                          onChange={(e) => handleInputChange("last_name", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Phone Number <span style={{color: 'red'}}>*</span></label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.phone_no[index]}
                          onChange={(e) => handleInputChange("phone_no", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Email <span style={{color: 'red'}}>*</span></label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email[index]}
                          onChange={(e) => handleInputChange("email", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Date of Birth <span style={{color: 'red'}}>*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.dob[index]}
                          onChange={(e) => handleInputChange("dob", e.target.value, index)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Sex <span style={{color: 'red'}}>*</span></label>
                        <select
                          className="form-select"
                          value={formData.sex[index]}
                          onChange={(e) => handleInputChange("sex", e.target.value, index)}
                        >
                          <option value="">Select Sex</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Relation <span style={{color: 'red'}}>*</span></label>
                        <select
                          className="form-select"
                          value={formData.relation[index]}
                          onChange={(e) => handleInputChange("relation", e.target.value, index)}
                        >
                          <option value="">Select Relation</option>
                          {relations.map((relation) => (
                            <option key={relation} value={relation}>
                              {relation}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Nationality <span style={{color: 'red'}}>*</span></label>
                        <select
                          className="form-select"
                          value={formData.nationality[index]}
                          onChange={(e) => handleInputChange("nationality", e.target.value, index)}
                        >
                          <option value="">Select Nationality</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Container>
  );
};

export default AddPlan;
